import { useLayoutEffect, useRef, useState, useSyncExternalStore } from 'react';
import { useAuth } from '@/context/AuthContext';
import { getActiveSessionRevision, subscribeActiveSessionRevision } from '@/api/authenticatedClient';
import { toast } from 'sonner';

const principal = binding => {
  const { user, source } = binding || {};
  return user?.id && user?.tenant_id && ['cloud', 'offline'].includes(source)
    ? JSON.stringify([user.tenant_id, user.id, source]) : null;
};

// TCA-26101404: one opening owns both its draft and every write continuation.
// Retirement prevents UI publication; it does not cancel/roll back server work.
export function useOrderEditorOperation(mode) {
  const { sessionBinding, getToken } = useAuth() || {};
  const revision = useSyncExternalStore(subscribeActiveSessionRevision, getActiveSessionRevision);
  const [opening] = useState(() => ({ binding: sessionBinding, principal: principal(sessionBinding), mode }));
  const latest = useRef(null);
  latest.current = { sessionBinding, mode };
  const mounted = useRef(false);
  const retired = useRef(false);
  const lock = useRef(null);
  const uncertain = useRef(null);
  const followUp = useRef(null);
  const reads = useRef(new Set());
  const [pending, setPending] = useState(null);
  const [unknown, setUnknown] = useState(null);
  const [notice, setNotice] = useState(null);
  const [refreshFailed, setRefreshFailed] = useState(false);
  const owns = () => mounted.current && !retired.current && !!opening.principal
    && opening.binding?.revision === getActiveSessionRevision()
    && latest.current.sessionBinding?.revision === opening.binding.revision
    && principal(latest.current.sessionBinding) === opening.principal
    && latest.current.mode === opening.mode;
  useLayoutEffect(() => {
    mounted.current = true;
    const controllers = reads.current;
    return () => { mounted.current = false; controllers.forEach(controller => controller.abort()); controllers.clear(); };
  }, []);
  const visible = !!opening.principal && opening.binding?.revision === revision
    && sessionBinding?.revision === opening.binding.revision
    && principal(sessionBinding) === opening.principal && mode === opening.mode;
  useLayoutEffect(() => {
    if (!visible) {
      reads.current.forEach(controller => controller.abort());
      reads.current.clear();
    }
  }, [visible]);
  const canAct = () => owns() && !lock.current && !uncertain.current && !followUp.current;
  const config = () => {
    if (!owns()) return null;
    const token = getToken?.();
    return token && owns() ? { headers: { Authorization: `Bearer ${token}` } } : null;
  };
  const read = async execute => {
    const request = config();
    if (!request) return undefined;
    const controller = new AbortController();
    reads.current.add(controller);
    try { return await execute({ ...request, signal: controller.signal }); }
    finally { reads.current.delete(controller); }
  };
  const run = async ({ label, execute, accepted, rejected, errorMessage = 'Update failed' }) => {
    if (!canAct()) return false;
    const request = config();
    if (!request) return false;
    const attempt = {};
    lock.current = attempt;
    setPending(label);
    setNotice(null);
    try {
      let result;
      try { result = await execute(request); }
      catch (error) {
        if (!owns()) return false;
        if (rejected?.(error)) return false;
        const status = error?.response?.status;
        if (!status || status === 408 || status >= 500) {
          const message = `${label}: result not confirmed. Check the saved record before repeating this action; it may already have succeeded.`;
          uncertain.current = message;
          setUnknown(message);
        } else {
          const detail = error?.response?.data?.detail;
          toast.error(typeof detail === 'string' ? detail : errorMessage);
        }
        return false;
      }
      if (!owns()) return false;
      // An acknowledged write is never relabelled a failed write by UI refresh.
      try { await accepted?.(result); }
      catch {
        if (owns()) {
          followUp.current = () => accepted?.(result);
          setRefreshFailed(true);
          setNotice('Saved, but the display could not refresh. Retry the refresh; do not repeat the saved action.');
        }
      }
      return true;
    } finally {
      if (lock.current === attempt) lock.current = null;
      if (owns()) setPending(null);
    }
  };
  const retryRefresh = async () => {
    if (!owns() || lock.current || !followUp.current) return;
    const attempt = {};
    lock.current = attempt;
    setPending('Refreshing saved changes');
    try {
      await followUp.current();
      if (owns()) {
        followUp.current = null;
        setRefreshFailed(false);
        setNotice(null);
      }
    } catch { /* Keep the acknowledged write and refresh-only retry. */ }
    finally {
      if (lock.current === attempt) lock.current = null;
      if (owns()) setPending(null);
    }
  };
  return {
    owns, visible, canAct, config, read, run, pending, unknown, notice, refreshFailed, retryRefresh,
    blocked: !!pending || !!unknown || refreshFailed || !visible,
    isPending: () => !!lock.current,
    retire: () => { retired.current = true; reads.current.forEach(controller => controller.abort()); reads.current.clear(); },
    report: message => { if (owns()) setNotice(message); },
    reviewedUnknown: () => {
      if (!owns() || lock.current) return;
      uncertain.current = null;
      setUnknown(null);
    },
  };
}
